import {
    assertInArray,
    assertMaximum,
    assertMinimum,
    assertStringOrNumber,
} from '../../validators';
import type { CommonAttributeSettings, CommonMetadataKeys } from './common';
import { createAttributeSetter, DEFAULT_ATTRIBUTE_SETTINGS, defineMetadata } from './common';

type StringOrNumberAttributeSettings = CommonAttributeSettings & {
    choices: string[];
    min: number;
    max: number;
};
export const INFERRED_NUMBER_ATTRIBUTE_TYPE = 'StringOrNumberAttributeType';
export type StringOrNumberMetadataKeys = keyof StringOrNumberAttributeSettings | CommonMetadataKeys;
const DEFAULT_SETTINGS: StringOrNumberAttributeSettings = {
    ...DEFAULT_ATTRIBUTE_SETTINGS,
    choices: [],
    min: -Infinity,
    max: Infinity,
};

/**
 * Creates an attribute decorator used to describe a string property. In addition to the shared
 * attribute settings, the string decorator allows validation to enforce the value is a member of inArray.
 */
export function StringOrNumberAttribute<T extends string | number>(
    settings: Partial<StringOrNumberAttributeSettings> = {},
) {
    const resolvedSettings: StringOrNumberAttributeSettings = { ...DEFAULT_SETTINGS, ...settings };
    const validator = (value: number | string) => {
        assertStringOrNumber(value);

        if (typeof value === 'string') {
            if (resolvedSettings.choices.length > 0) {
                assertInArray(value, resolvedSettings.choices);
            }
        } else {
            assertMinimum(value, resolvedSettings.min);
            assertMaximum(value, resolvedSettings.max);
        }
    };
    return function <This>(
        { get, set }: ClassAccessorDecoratorTarget<This, T>,
        context: ClassAccessorDecoratorContext,
    ): ClassAccessorDecoratorResult<This, T> {
        defineMetadata(
            resolvedSettings,
            INFERRED_NUMBER_ATTRIBUTE_TYPE,
            context,
            get as (this: unknown) => unknown,
        );
        return {
            get: function (this: This) {
                return get.call(this);
            },
            set: createAttributeSetter(
                validator,
                get as (this: object) => T,
                set as (this: object, value: T) => void,
            ) as (this: This, value: T) => void,
        };
    };
}
